import { Link, useParams } from "react-router-dom";
import CameraScreen from "../../components/CameraScreen/CameraScreen";
import useGuestImagesUploadCount from "../../hooks/useGuestImagesUploadCount";
import useEventStatus from "../../hooks/useEventStatus";
import { useUserProfileContext } from "../../components/ProtectedRoute";
import "./UploadEventImages.css";
import { useMemo } from "react";

// TODO: Get total uploads allowed from API or configuration based on plans
const BASIC_PLAN_TOTAL_UPLOADS = 5;

/**
 * UploadEventImages component - Main entry point for QR-based camera flow
 *
 * Features:
 * - Validates event expiration status before allowing access
 * - Fetches user's image upload statistics from API
 * - Calculates remaining uploads for current event
 * - Provides retry functionality for API failures
 *
 * @returns {JSX.Element} - QR flow interface with camera or loading/error states
 */
const UploadEventImages = () => {
  // Extract event_code from URL parameter
  const { event_code } = useParams();

  // Fetch event status to check expiration status
  const {
    loading: eventLoading,
    error: eventError,
    isEventExpired,
    status,
    canUploadToEvent,
    refetch,
    event_id,
  } = useEventStatus(event_code);

  // Fetch user statistics including image upload counts
  const { guestImagesCount, error, loading, fetchGuestImagesCount } =
    useGuestImagesUploadCount();

  // Get user profile loading state from context
  const { loading: profileLoading } = useUserProfileContext();

  // Memoize uploaded count calculation to prevent unnecessary recalculations
  const uploadedCount = useMemo(() => {
    return (
      guestImagesCount?.data?.images_uploaded?.by_event?.find(
        (event) => event.event_id === event_id,
      )?.count ?? 0
    );
  }, [guestImagesCount, event_id]);

  // Memoize canUpload calculation
  const canUpload = useMemo(
    () => canUploadToEvent && uploadedCount < BASIC_PLAN_TOTAL_UPLOADS,
    [canUploadToEvent, uploadedCount],
  );

  // Memoize loading state
  const isLoading = useMemo(
    () => eventLoading || loading || profileLoading,
    [eventLoading, loading, profileLoading],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (eventError || !canUploadToEvent || error) {
    const upcoming = status === "upcoming";
    const completed = status === "completed" || isEventExpired;
    const hasError = Boolean(eventError || (canUploadToEvent && error));
    const title = completed ? "Event completed" : upcoming ? "Event not yet started" : hasError ? "Unable to load uploads" : "Uploads unavailable";
    const message =completed
      ? "This event has ended. Photo uploads are now closed."
      : upcoming ? "This event hasn’t started yet. You can upload photos once it begins."
      :  hasError ? eventError || error : "This event is not currently accepting photo uploads.";
    return (
      <main className="loading-container">
        <section className="upload-status-card" role={hasError ? "alert" : "status"}>
          <h1>{title}</h1>
          <p>{message}</p>
          {(hasError || upcoming) && <button type="button" className="retry-button" onClick={() => { refetch(); fetchGuestImagesCount(); }}>Try again</button>}
          <Link to="/" className="upload-status-home">Back to home</Link>
        </section>
      </main>
    );
  }

  return (
    <div className="loading-container">
      <CameraScreen
        totalUploads={BASIC_PLAN_TOTAL_UPLOADS}
        uploadedCount={uploadedCount}
        canUpload={canUpload}
        fetchGuestImagesCount={fetchGuestImagesCount}
        event_id={event_id}
      />
    </div>
  );
};

export default UploadEventImages;
